import { get as awsGet, post as awsPost, put as awsPut, del as awsDel } from "aws-amplify/api";
import { RestApiResponse } from "@aws-amplify/api-rest/dist/esm/types";
import Serializer, { defaultSerializerOptions, getModelSerializer } from "@fableous/core/util/Serialization";
import { DocumentType } from "@aws-amplify/core/internals/utils";
import logger from "./errorLib";
import { fetchAuthSession } from "@aws-amplify/core";
import { BasicError } from "@fableous/core/model/BasicError";
import { backOff } from "exponential-backoff";

let globalErrorCount = 0;
const MAX_FAILURES = 3;
let isCircuitTripped = false;
export default class ApiClient {
	serializer: Serializer;
	service: string;

	constructor(service: string) {
		this.service = service;
		this.serializer = getModelSerializer();
	}
	 
	parseError(e: any, logErrors: boolean = true) {
		//TODO - amplify doesn't work like this anymore
		let errorObj;
		if (e.response && e.response.data && e.response.data.serializedError) {
			errorObj = this.serializer.deserialize(e.response.data.serializedError);
			if (logErrors) logger.error(errorObj);
			throw errorObj;
		} else if (e.response && e.response.data && e.response.data.error) {
			if (logErrors) logger.error(e);
			throw e;
		} else {
			if (logErrors) logger.error(e);
			throw e;
		}
	}
	async getHeaders(requireSub : boolean = true): Promise<Record<string, string>> {
		const session = await fetchAuthSession();
		const sub = session.tokens?.idToken?.payload.sub;
		const identityId = session.identityId;
		const region = session.tokens?.idToken?.payload?.iss?.split(".")[1];
		if (!sub || !region || !identityId) {
			if(!requireSub) return {};
			throw new BasicError("No sub", "No user sub found in auth session");
		}
		return {
			"x-cognito-sub": sub,
			"x-region": region,
			"x-identity-id": identityId
		};
	}
	async get<T>(path: string, queryParams?: Record<string, string>, logErrors: boolean = true): Promise<T> {
		if(isCircuitTripped) throw new BasicError("Connection Error", "There is a connection error, please try refreshing the page");
		return this.withBackoff(async() => {
			try {
				const result: RestApiResponse = await awsGet({
					apiName: this.service,
					path,
					options: {
						queryParams,
						headers: await this.getHeaders(),
					},
				}).response;
				const deserialised = this.serializer.deserialize((await result.body.json()) as object);
				return deserialised as T;
			} catch (e) {
				this.parseError(e, logErrors);
				throw e;
			}
		});
	}
	async post<T, V = T>(path: string, object?: T, logErrors: boolean = true, requireSub : boolean = true): Promise<V> {
		if(isCircuitTripped) throw new BasicError("Connection Error", "There is a connection error, please try refreshing the page");
		return this.withBackoff(async() => {
			try {
				let result: RestApiResponse;
				if (object) {
					const serializedBody: DocumentType | FormData = this.serializer.serialize(object, {
						...defaultSerializerOptions,
						anonymiseFirstLevel: false,
					}) as DocumentType;
					result = await awsPost({ apiName: this.service, path, options: { body: serializedBody, headers: await this.getHeaders(requireSub) } }).response;
				} else {
					result = await awsPost({ apiName: this.service, path, options: { body: object as DocumentType, headers: await this.getHeaders(requireSub) } }).response;
				}
				const deserialised = this.serializer.deserialize((await result.body.json()) as object);
				return deserialised as V;
			} catch (e) {
				this.parseError(e, logErrors);
				throw e;
			}
		});
	}
	async put<T>(path: string, object: T | unknown, serializeInput = true, logErrors: boolean = true): Promise<T> {
		if(isCircuitTripped) throw new BasicError("Connection Error", "There is a connection error, please try refreshing the page");
		return this.withBackoff(async() => {
			try {
				let result: RestApiResponse;
				if (object) {
					let body: DocumentType | FormData;
					if (serializeInput) {
						body = this.serializer.serialize(object, { ...defaultSerializerOptions, anonymiseFirstLevel: false }) as DocumentType;
					} else {
						body = object as FormData;
					}
					result = await awsPut({ apiName: this.service, path, options: { body, headers: await this.getHeaders() } }).response;
				} else {
					result = await awsPut({ apiName: this.service, path, options: { headers: await this.getHeaders() } }).response;
				}
				const deserialised = this.serializer.deserialize((await result.body.json()) as object);
				return deserialised as T;
			} catch (e) {
				this.parseError(e, logErrors);
				throw e;
			}
		});
	}
	async del(path: string, logErrors: boolean = true): Promise<void> {
		if(isCircuitTripped) throw new BasicError("Connection Error", "There is a connection error, please try refreshing the page");
		return this.withBackoff(async() => {
			try {
				await awsDel({ apiName: this.service, path, options: { headers: await this.getHeaders() } }).response;
			} catch (e) {
				this.parseError(e, logErrors);
				throw e;
			}
		});
	}
	async withBackoff<T>(
		task: () => Promise<T>,
		options = {
			numOfAttempts: 3,
			startingDelay: 500,
			timeMultiple: 2,
			jitter: "full" as const,
			retry: (e: unknown) => {
				if(isCircuitTripped) return false;
				globalErrorCount++;
				if (globalErrorCount >= MAX_FAILURES) {
					isCircuitTripped = true;
				}
				// Retry on network or 5xx errors
				return e instanceof Error && /5\d\d|Network/.test(e.message);
			},
		}
	): Promise<T> {
		return backOff(task, options);
	}
}
